
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useCurrency } from '../../hooks/useCurrency';
import { useLanguage } from '../../hooks/useLanguage';
import { EXCHANGE_RATES } from '../../lib/currency';

interface HeaderProps {
  onSearch?: (query: string) => void;
}

const CURRENCY_OPTIONS = [
  { code: 'COP' as const, label: 'COP', symbol: '$', flag: '🇨🇴', name: 'Peso Colombiano' },
  { code: 'USD' as const, label: 'USD', symbol: '$', flag: '🇺🇸', name: 'Dólar Americano' },
  { code: 'EUR' as const, label: 'EUR', symbol: '€', flag: '🇪🇺', name: 'Euro' },
];

const LANGUAGE_OPTIONS = [
  { code: 'es' as const, label: 'ES', flag: '🇪🇸', name: 'Español' },
  { code: 'en' as const, label: 'EN', flag: '🇺🇸', name: 'English' },
];

export default function Header({ onSearch }: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [showMobileCurrencyDropdown, setShowMobileCurrencyDropdown] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showMobileLangDropdown, setShowMobileLangDropdown] = useState(false);
  const { totalItems, totalPrice } = useCart();
  const { currency, setCurrency } = useCurrency();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLInputElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);
  const mobileCurrencyRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const mobileLangRef = useRef<HTMLDivElement>(null);

  const activeCurrency = CURRENCY_OPTIONS.find(c => c.code === currency) || CURRENCY_OPTIONS[0];
  const activeLanguage = LANGUAGE_OPTIONS.find(l => l.code === language) || LANGUAGE_OPTIONS[0];

  useEffect(() => {
    const handleScroll = () => {
      try {
        setIsScrolled(window.scrollY > 50);
      } catch (e) {
        console.warn('Scroll listener could not be attached:', e);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setShowCurrencyDropdown(false);
      }
      if (mobileCurrencyRef.current && !mobileCurrencyRef.current.contains(event.target as Node)) {
        setShowMobileCurrencyDropdown(false);
      }
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setShowLangDropdown(false);
      }
      if (mobileLangRef.current && !mobileLangRef.current.contains(event.target as Node)) {
        setShowMobileLangDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus mobile search input when toggled open
  useEffect(() => {
    if (showMobileSearch && mobileSearchRef.current) {
      mobileSearchRef.current.focus();
    }
  }, [showMobileSearch]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setShowMobileSearch(false);
    setShowCurrencyDropdown(false);
    setShowMobileCurrencyDropdown(false);
    setShowLangDropdown(false);
    setShowMobileLangDropdown(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowSuggestions(false);
      setShowMobileSearch(false);
      if (location.pathname !== '/') {
        navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      } else {
        if (onSearch) {
          onSearch(searchQuery.trim());
        }
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSuggestions(query.trim().length > 0);
    
    if (location.pathname === '/' && onSearch) {
      onSearch(query);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    setShowMobileSearch(false);
    
    if (location.pathname !== '/') {
      navigate(`/?search=${encodeURIComponent(suggestion)}`);
    } else {
      if (onSearch) {
        onSearch(suggestion);
      }
    }
  };

  const handleCurrencySelect = (code: 'COP' | 'USD' | 'EUR') => {
    setCurrency(code);
    setShowCurrencyDropdown(false);
    setShowMobileCurrencyDropdown(false);
  };

  const handleLanguageSelect = (code: 'es' | 'en') => {
    setLanguage(code);
    setShowLangDropdown(false);
    setShowMobileLangDropdown(false);
  };

  const formatPrice = (price: number) => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price * EXCHANGE_RATES.COP_TO_USD);
    }
    if (currency === 'EUR') {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price * EXCHANGE_RATES.COP_TO_EUR);
    }
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const categoryLinks = [
    { label: t('nav.home'), href: '/', icon: 'ri-home-line' },
    { label: t('nav.windows'), href: '/windows', icon: 'ri-windows-fill' },
    { label: t('nav.office'), href: '/office', icon: 'ri-file-word-line' },
    { label: t('nav.antivirus'), href: '/antivirus', icon: 'ri-shield-check-line' },
    { label: t('nav.vpn'), href: '/vpn', icon: 'ri-global-line' },
    { label: '🎓 Pack Estudiante', href: '/estudiantes', icon: 'ri-graduation-cap-line' },
    { label: t('nav.testimonials'), href: '/testimonios', icon: 'ri-chat-quote-line' },
  ];

  const allSuggestions = [
    'Windows 10 PRO',
    'Windows 11 PRO',
    'Windows 11 HOME',
    'Office 2024 Professional Plus',
    'Office 2021 Professional Plus',
    'Office 2024 Home and Business',
    'Office 2021 Home and Business',
    'Office MAC',
    'Kaspersky Standard',
    'Kaspersky Premium',
    'Kaspersky Plus',
    'McAfee Total Protection',
    'ESET NOD32',
    'AdGuard VPN',
    'VPN',
    'Antivirus',
  ];

  const filteredSuggestions = searchQuery.trim()
    ? allSuggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 6)
    : [];

  return (
    <header className="w-full bg-white shadow-sm sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-gradient-to-r from-[#005a9e] to-[#0078D4] text-white py-2.5 px-4 text-sm hidden lg:block">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-store-2-line text-white/80 w-4 h-4 flex items-center justify-center"></i>
            <span className="text-white/90">{t('header.store_question')}</span>
            <Link
              to="/distribuidor"
              className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 px-3 py-0.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ml-1"
            >
              <i className="ri-rocket-line text-xs"></i>
              {t('header.become_distributor')}
            </Link>
            <span className="text-white/90">{t('header.special_prices')}</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Selector Desktop */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 pl-2.5 pr-2 py-1 rounded-full transition-all cursor-pointer whitespace-nowrap group"
                title={language === 'es' ? 'Cambiar idioma' : 'Change language'}
              >
                <span className="text-base leading-none">{activeLanguage.flag}</span>
                <span className="text-xs font-bold tracking-wide">{activeLanguage.label}</span>
                <i className={`ri-arrow-down-s-line text-sm opacity-70 transition-transform duration-200 ${showLangDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {showLangDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden w-52 z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                      {language === 'es' ? 'Idioma' : 'Language'}
                    </p>
                  </div>
                  <div className="py-1">
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <button
                        key={opt.code}
                        onClick={() => handleLanguageSelect(opt.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                          language === opt.code
                            ? 'bg-[#0078D4]/5'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-xl leading-none">{opt.flag}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${language === opt.code ? 'text-[#0078D4]' : 'text-gray-800'}`}>
                              {opt.name}
                            </span>
                          </div>
                        </div>
                        {language === opt.code && (
                          <div className="w-5 h-5 bg-[#0078D4] rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Currency Selector Desktop */}
            <div ref={currencyRef} className="relative">
              <button
                onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 pl-2.5 pr-2 py-1 rounded-full transition-all cursor-pointer whitespace-nowrap group"
                title={t('header.select_currency')}
              >
                <span className="text-base leading-none">{activeCurrency.flag}</span>
                <span className="text-xs font-bold tracking-wide">{activeCurrency.label}</span>
                <span className="text-[10px] opacity-70">{activeCurrency.symbol}</span>
                <i className={`ri-arrow-down-s-line text-sm opacity-70 transition-transform duration-200 ${showCurrencyDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {showCurrencyDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden w-64 z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                    <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('header.select_currency')}</p>
                  </div>
                  <div className="py-1">
                    {CURRENCY_OPTIONS.map((opt) => (
                      <button
                        key={opt.code}
                        onClick={() => handleCurrencySelect(opt.code)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                          currency === opt.code
                            ? 'bg-[#0078D4]/5'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-xl leading-none">{opt.flag}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${currency === opt.code ? 'text-[#0078D4]' : 'text-gray-800'}`}>
                              {opt.label}
                            </span>
                            <span className="text-xs text-gray-400">{opt.symbol}</span>
                          </div>
                          <span className="text-[11px] text-gray-500">{opt.name}</span>
                        </div>
                        {currency === opt.code && (
                          <div className="w-5 h-5 bg-[#0078D4] rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                    <p className="text-[10px] text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {t('header.rates_updated')}: {EXCHANGE_RATES.lastUpdated}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/login"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-1 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-line text-sm"></i>
              <span>{t('header.distributor_access')}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-4">
        <div className="flex items-center justify-between gap-1.5 sm:gap-2">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0 min-w-0 max-w-[55%] sm:max-w-none">
            <img
              src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/7cae0096c2ae72bfc5e3e26071deff45.jpeg"
              alt="Digital Licence Store"
              className="h-8 sm:h-12 w-auto flex-shrink-0"
            />
            <div className="flex flex-col min-w-0">
              <span className="text-xs sm:text-xl font-bold text-gray-900 whitespace-nowrap truncate">
                Digital Licence Store
              </span>
              <span className="text-[8px] sm:text-xs text-teal-600 italic font-medium tracking-wide whitespace-nowrap truncate">
                ✦ {language === 'es' ? 'Licencias Originales al Mejor Precio' : 'Original Licenses at the Best Price'}
              </span>
            </div>
          </Link>

          {/* Desktop Search & Icons */}
          <div className="hidden lg:flex items-center gap-4 flex-1 max-w-2xl mx-8">
            <div ref={searchRef} className="flex-1 relative">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="search"
                  placeholder={t('header.search_placeholder')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4] text-sm"
                />
                <button 
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0078D4] cursor-pointer"
                >
                  <i className="ri-search-line text-lg"></i>
                </button>
              </form>

              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  <div className="py-2">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">
                      {t('header.suggestions')}
                    </div>
                    {filteredSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer transition-colors"
                      >
                        <i className="ri-search-line text-gray-400"></i>
                        <span>{suggestion}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
            {/* Mobile language selector */}
            <div ref={mobileLangRef} className="relative lg:hidden">
              <button
                onClick={() => setShowMobileLangDropdown(!showMobileLangDropdown)}
                className="flex items-center gap-0.5 text-gray-700 hover:text-[#0078D4] cursor-pointer border border-gray-200 rounded-full px-1.5 py-0.5 sm:px-2 sm:py-1 transition-colors"
                title={language === 'es' ? 'Cambiar idioma' : 'Change language'}
              >
                <span className="text-xs sm:text-sm leading-none">{activeLanguage.flag}</span>
                <span className="text-[10px] sm:text-[11px] font-bold">{activeLanguage.label}</span>
              </button>

              {showMobileLangDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden w-44 z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                      {language === 'es' ? 'Idioma' : 'Language'}
                    </p>
                  </div>
                  <div className="py-1">
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <button
                        key={opt.code}
                        onClick={() => handleLanguageSelect(opt.code)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors cursor-pointer ${
                          language === opt.code
                            ? 'bg-[#0078D4]/5'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg leading-none">{opt.flag}</span>
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm font-bold ${language === opt.code ? 'text-[#0078D4]' : 'text-gray-800'}`}>
                            {opt.name}
                          </span>
                        </div>
                        {language === opt.code && (
                          <div className="w-4 h-4 bg-[#0078D4] rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-[10px]"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile currency selector */}
            <div ref={mobileCurrencyRef} className="relative lg:hidden">
              <button
                onClick={() => setShowMobileCurrencyDropdown(!showMobileCurrencyDropdown)}
                className="flex items-center gap-0.5 text-gray-700 hover:text-[#0078D4] cursor-pointer border border-gray-200 rounded-full px-1.5 py-0.5 sm:px-2 sm:py-1 transition-colors"
                title={t('header.select_currency')}
              >
                <span className="text-xs sm:text-sm leading-none">{activeCurrency.flag}</span>
                <span className="text-[10px] sm:text-[11px] font-bold">{activeCurrency.label}</span>
                <i className={`ri-arrow-down-s-line text-[10px] sm:text-xs opacity-60 transition-transform duration-200 ${showMobileCurrencyDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {showMobileCurrencyDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden w-56 z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{t('header.currency')}</p>
                  </div>
                  <div className="py-1">
                    {CURRENCY_OPTIONS.map((opt) => (
                      <button
                        key={opt.code}
                        onClick={() => handleCurrencySelect(opt.code)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors cursor-pointer ${
                          currency === opt.code
                            ? 'bg-[#0078D4]/5'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg leading-none">{opt.flag}</span>
                        <div className="flex-1 min-w-0">
                          <span className={`text-sm font-bold ${currency === opt.code ? 'text-[#0078D4]' : 'text-gray-800'}`}>
                            {opt.label}
                          </span>
                          <span className="text-[10px] text-gray-400 ml-1.5">{opt.symbol}</span>
                        </div>
                        {currency === opt.code && (
                          <div className="w-4 h-4 bg-[#0078D4] rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-check-line text-white text-[10px]"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile search toggle */}
            <button 
              className="lg:hidden text-gray-700 hover:text-[#0078D4] cursor-pointer w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center"
              onClick={() => setShowMobileSearch(!showMobileSearch)}
            >
              <i className={`${showMobileSearch ? 'ri-close-line' : 'ri-search-line'} text-lg sm:text-xl`}></i>
            </button>

            <Link
              to="/carrito"
              className="flex items-center gap-1 sm:gap-2 text-gray-700 hover:text-[#0078D4] relative cursor-pointer"
            >
              <div className="relative">
                <i className="ri-shopping-cart-line text-lg sm:text-xl"></i>
                <span className="absolute -top-2 -right-2 sm:-top-2.5 sm:-right-2.5 bg-[#0078D4] text-white text-[9px] sm:text-[10px] rounded-full w-4 h-4 sm:w-[18px] sm:h-[18px] flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              </div>
              <span className="hidden lg:inline text-sm whitespace-nowrap ml-2">{formatPrice(totalPrice)}</span>
            </Link>

            <button
              className="lg:hidden text-gray-700 hover:text-[#0078D4] cursor-pointer w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl sm:text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar */}
      {showMobileSearch && (
        <div className="lg:hidden border-t border-gray-100 bg-white px-3 py-3 animate-[slideDown_0.2s_ease-out]">
          <form onSubmit={handleSearch} className="relative">
            <input
              ref={mobileSearchRef}
              type="search"
              placeholder={t('header.search_placeholder')}
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078D4] text-sm bg-gray-50"
            />
            <button 
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#0078D4] cursor-pointer"
            >
              <i className="ri-search-line text-lg"></i>
            </button>
          </form>
          {/* Mobile suggestions */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                >
                  <i className="ri-search-line text-gray-400"></i>
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trust Badges Bar */}
      <div className="bg-white border-t border-gray-100 hidden lg:block">
        <div className="container mx-auto px-4">
          {/* Removed trust badges section */}
        </div>
      </div>

      {/* Category Navigation Menu */}
      <nav className={`bg-gray-50 border-t border-gray-200 ${isMenuOpen ? 'block' : 'hidden'} lg:block`}>
        <div className="container mx-auto px-3 sm:px-4">
          <ul className="flex flex-col lg:flex-row lg:items-center lg:justify-center gap-0 lg:gap-2">
            {categoryLinks.map((item, index) => (
              <li key={index}>
                <Link
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3.5 lg:py-3 text-gray-700 hover:text-[#0078D4] hover:bg-gray-100 font-medium text-sm whitespace-nowrap cursor-pointer transition-colors rounded-lg ${
                    location.pathname === item.href ? 'text-[#0078D4] bg-[#0078D4]/5' : ''
                  }`}
                >
                  <i className={`${item.icon} text-lg w-5 h-5 flex items-center justify-center`}></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
            {/* Mobile-only links */}
            <li className="lg:hidden border-t border-gray-200 mt-1 pt-1">
              <Link
                to="/distribuidor"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3.5 text-[#0078D4] hover:bg-gray-100 font-medium text-sm whitespace-nowrap cursor-pointer transition-colors rounded-lg"
              >
                <i className="ri-rocket-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>{t('nav.become_distributor')}</span>
              </Link>
            </li>
            <li className="lg:hidden">
              <Link
                to="/login"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3.5 text-gray-700 hover:text-[#0078D4] hover:bg-gray-100 font-medium text-sm whitespace-nowrap cursor-pointer transition-colors rounded-lg"
              >
                <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>{t('nav.distributor_access')}</span>
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
}
